// ============================================================
// STORY MODE — runtime controller
// ============================================================
//
// Walks a `ChapterDef.script` linearly, exposing pause / hint /
// constraint state to the rest of the game. Game.ts queries the
// controller at key boundaries:
//
//   shouldPauseGame()          — block input + auto-progress while
//                                dialogue is on screen
//   isSwapAllowed(...)         — gate handleSwap during forced
//                                tutorial moves
//   isSpellAllowed(slot,el,p)  — gate selectSpell / confirmTarget
//   advanceDialogue()          — called when player taps the
//                                dialogue overlay
//   consumePendingDeployment() — Game pulls preset units out at
//                                DEPLOY entry
//   consumePendingBoard()      — Game resets M3 board before each
//                                forced swap
//   onSwapApplied(...)         — hook from Game.handleSwap success
//   onMatchSettle()            — hook after cascade animations done
//   onSpellApplied(...)        — hook from Game.confirmTarget
//   onSchedConfirmed()         — hook from Game._finalizeSched
//   observeBattleEvent(ev)     — hook from Game._applyEvent for
//                                conditional tips
//   onGameOver(winner)         — switch to post / lose script
//
// Internal state is intentionally just "current script index +
// active dialogue queue + pending afterLines" — easy to reason
// about and easy to reset for retries.
// ============================================================

import type {
  ChapterDef, DialogueLine, PresetBoard, PresetUnit,
  TutorialStep, ConditionalTip, ExecPauseDef,
} from './StoryTypes.ts';
import type { Pos } from '../BattleUnit.ts';
import type { SpellCommand } from '../server/types.ts';
import * as StoryProgress from './StoryProgress.ts';

type Phase = 'main' | 'post' | 'lose' | 'done';

interface ExpectedSwap { r1: number; c1: number; r2: number; c2: number; hint?: string }
interface ExpectedSpell {
  slot: 0 | 1 | 2;
  el: number;
  /** null = any target accepted (free pick); Pos = exact match required. */
  targetPos: Pos | null;
  hint?: string;
}
interface ArrowHint { from: Pos; to: Pos; label?: string }
interface ExpectedPlace { type: string; hintPos: Pos; hint?: string }
/** Path is satisfied when player's drawn path equals `cells` exactly (in order). */
interface ExpectedPath { unitPos: Pos; cells: Pos[]; hint?: string }

export class StoryController {
  readonly chapter: ChapterDef;

  // --- Script position ---
  private scriptIdx = 0;
  private phase: Phase = 'main';

  // --- Active dialogue overlay state ---
  dialogueActive = false;
  dialogueLines: DialogueLine[] = [];
  dialogueIdx = 0;

  // --- Things the rest of the game polls / consumes ---
  /** Set when player should be force-deployed. Game consumes once at DEPLOY entry. */
  private pendingDeployment: { player: PresetUnit[]; ai: PresetUnit[] } | null = null;
  /** Set when M3 board should be reset before next swap. */
  private pendingBoard: PresetBoard | null = null;
  /** Active swap constraint — non-null = only this swap allowed. */
  expectedSwap: ExpectedSwap | null = null;
  /** Active spell constraint — non-null = only this spell selection allowed. */
  expectedSpell: ExpectedSpell | null = null;
  /** Active deploy-placement constraint — only this `type` at `hintPos`. */
  expectedPlace: ExpectedPlace | null = null;
  /** Active deploy-path constraint — exact cell sequence required. */
  expectedPath: ExpectedPath | null = null;
  /** True when the tutorial wants the player to press "Start Battle". */
  expectedStartBattle = false;
  /** True when the tutorial wants the player to press "Confirm Plan". */
  expectedConfirmSched = false;
  /** True when the tutorial wants the player to tap the "Draw Paths" toggle. */
  expectedPathToggle = false;
  /** Arrow + label rendered over the battlefield. */
  arrowHint: ArrowHint | null = null;
  /** True once script reaches `freePlay` step. */
  freePlay = false;

  /** Lines queued to show AFTER a constraint is satisfied (and after cascade settles). */
  private queuedAfterLines: DialogueLine[] | null = null;
  /** Constraint to ACTIVATE after the current dialogue is dismissed. */
  private armedSwap: ExpectedSwap | null = null;
  private armedSpell: ExpectedSpell | null = null;
  private armedPlace: ExpectedPlace | null = null;
  private armedPath: ExpectedPath | null = null;
  private armedStartBattle = false;
  private armedConfirmSched = false;
  private armedPathToggle = false;

  // --- Conditional tips (free-play hints) ---
  private shownTips: Set<string> = new Set();
  /** EXEC pauses already shown (keyed by tick:type[:phase]). Cleared on start(). */
  private firedExecPauses: Set<string> = new Set();
  /** Set by a `waitForPhase` script step; cleared by notifyPhaseEntered. */
  private waitingForPhase: 'match3' | 'sched' | 'exec' | 'over' | null = null;

  /** Game over reached — true means we're in post/lose script playback. */
  battleOver = false;
  /** True after lose-script dialogue has been dismissed — Game shows Retry button. */
  loseScriptDone = false;

  constructor(chapter: ChapterDef) {
    this.chapter = chapter;
  }

  // ----------------------------------------------------------
  // Lifecycle
  // ----------------------------------------------------------

  /** Reset to start of script. Used by start AND retry-after-loss. */
  start(): void {
    this.scriptIdx = 0;
    this.phase = 'main';
    this.dialogueActive = false;
    this.dialogueLines = [];
    this.dialogueIdx = 0;
    this.pendingDeployment = null;
    this.pendingBoard = null;
    this.expectedSwap = null;
    this.expectedSpell = null;
    this.expectedPlace = null;
    this.expectedPath = null;
    this.expectedStartBattle = false;
    this.expectedConfirmSched = false;
    this.expectedPathToggle = false;
    this.armedSwap = null;
    this.armedSpell = null;
    this.armedPlace = null;
    this.armedPath = null;
    this.armedStartBattle = false;
    this.armedConfirmSched = false;
    this.armedPathToggle = false;
    this.queuedAfterLines = null;
    this.arrowHint = null;
    this.freePlay = false;
    this.shownTips.clear();
    this.firedExecPauses.clear();
    this.waitingForPhase = null;
    this.battleOver = false;
    this.loseScriptDone = false;
    this._processStep();
  }

  // ----------------------------------------------------------
  // Public queries (Renderer + Game read these)
  // ----------------------------------------------------------

  /** True while a dialogue overlay is shown (game should ignore swipes / taps elsewhere). */
  shouldPauseGame(): boolean {
    return this.dialogueActive;
  }

  /**
   * True if the script has finished pre-battle (DEPLOY-phase) steps and
   * the next pending step belongs to MATCH3/SCHED/free-play. Game polls
   * this in `update()`'s DEPLOY branch to auto-trigger `startBattle()`.
   *
   * Returns FALSE while any DEPLOY-phase constraint is active
   * (expectPlace / expectPath / expectStartBattle) so the player has to
   * complete those steps manually.
   */
  shouldStartBattle(): boolean {
    if (this.phase !== 'main') return false;
    if (this.dialogueActive) return false;
    if (this.pendingDeployment) return false;
    // Player still has DEPLOY-phase work to do — never auto-start.
    if (this.expectedPlace || this.expectedPath || this.expectedStartBattle || this.expectedPathToggle) return false;
    const script = this.chapter.script;
    if (this.scriptIdx >= script.length) return true; // script exhausted
    const step = script[this.scriptIdx];
    return step.kind === 'presetBoard'
      || step.kind === 'expectSwap'
      || step.kind === 'expectSpell'
      || step.kind === 'expectAnySpell'
      || step.kind === 'expectConfirmSched'
      || step.kind === 'freePlay';
  }

  /** Returns true if the swap satisfies the current constraint (or no constraint). */
  isSwapAllowed(r1: number, c1: number, r2: number, c2: number): boolean {
    if (this.dialogueActive) return false;
    if (!this.expectedSwap) return true;
    const e = this.expectedSwap;
    return (
      (e.r1 === r1 && e.c1 === c1 && e.r2 === r2 && e.c2 === c2) ||
      (e.r1 === r2 && e.c1 === c2 && e.r2 === r1 && e.c2 === c1)
    );
  }

  /** Returns true if (slot, el) selection (no targetPos check yet) satisfies the constraint.
   *  `expectedSpell.el === -1` is the wildcard set by `expectAnySpell`. */
  isSpellElAllowed(slot: number, el: number): boolean {
    if (this.dialogueActive) return false;
    if (!this.expectedSpell) return true;
    if (this.expectedSpell.slot !== slot) return false;
    if (this.expectedSpell.el === -1) return true;
    return this.expectedSpell.el === el;
  }

  /** Returns true if (slot, el, targetPos) fully satisfies the constraint.
   *  expectedSpell.targetPos === null means "any target accepted". */
  isSpellTargetAllowed(slot: number, el: number, target: Pos): boolean {
    if (this.dialogueActive) return false;
    if (!this.expectedSpell) return true;
    const e = this.expectedSpell;
    if (e.slot !== slot) return false;
    if (e.el !== -1 && e.el !== el) return false;
    if (e.targetPos === null) return true; // any target
    return e.targetPos.x === target.x && e.targetPos.y === target.y;
  }

  /** Returns true if player can place `type` at (x, y).
   *  Same defensive rule as `isPathPointAllowed`: story mode has no
   *  "free placement" phase, so a missing constraint means "not yet
   *  allowed". Today Maroon5 has only one expectPlace + palette empties
   *  out after, so this is mostly future-proofing for multi-place chapters. */
  isPlaceAllowed(type: string, x: number, y: number): boolean {
    if (this.dialogueActive) return false;
    if (!this.expectedPlace) return false;
    return this.expectedPlace.type === type
        && this.expectedPlace.hintPos.x === x
        && this.expectedPlace.hintPos.y === y;
  }

  /** Returns true if (x, y) is the next cell in the expected path.
   *  In story mode there's no "free path drawing" phase — path edits
   *  are ONLY valid during an active `expectPath` step. Returning false
   *  when no constraint is active prevents off-script doodling that
   *  would scramble the unit's path before battle. */
  isPathPointAllowed(currentPathLen: number, x: number, y: number): boolean {
    if (this.dialogueActive) return false;
    if (!this.expectedPath) return false;
    if (currentPathLen >= this.expectedPath.cells.length) return false;
    const next = this.expectedPath.cells[currentPathLen];
    return next.x === x && next.y === y;
  }

  /** Returns true if player can press Start Battle now. */
  isStartBattleAllowed(): boolean {
    if (this.dialogueActive) return false;
    if (this.expectedStartBattle) return true;
    // If no expectStartBattle is active AND no other constraint is pending,
    // assume the script is past the deploy-teaching phase (free for the
    // player to start). Returns false ONLY while a deploy constraint is
    // explicitly pending.
    return !this.expectedPlace && !this.expectedPath && !this.expectedPathToggle;
  }

  /** Returns true if player can press Confirm Plan now. */
  isConfirmSchedAllowed(): boolean {
    if (this.dialogueActive) return false;
    if (this.expectedConfirmSched) return true;
    return !this.expectedSpell;
  }

  // ----------------------------------------------------------
  // Game-side hooks
  // ----------------------------------------------------------

  /** Game calls at DEPLOY entry — returns preset units (or null if none pending).
   *  Also walks the script forward to the next pending step (arrow hint /
   *  dialogue / etc) so the caller doesn't have to. */
  consumePendingDeployment(): { player: PresetUnit[]; ai: PresetUnit[] } | null {
    const p = this.pendingDeployment;
    this.pendingDeployment = null;
    if (p) this._processStep();
    return p;
  }

  /** Game calls before each M3 swap acceptance — returns preset board (or null).
   *  Walks forward to the next pending step on consumption. */
  consumePendingBoard(): PresetBoard | null {
    const b = this.pendingBoard;
    this.pendingBoard = null;
    if (b) this._processStep();
    return b;
  }

  /** Player tapped the dialogue overlay — advance to next line / dismiss. */
  advanceDialogue(): void {
    if (!this.dialogueActive) return;
    this.dialogueIdx++;
    if (this.dialogueIdx < this.dialogueLines.length) return;
    // Dialogue dismissed
    this.dialogueActive = false;
    this.dialogueLines = [];
    this.dialogueIdx = 0;
    // Activate any armed constraints (from `expect*` step that had `lines`)
    if (this.armedSwap) {
      this.expectedSwap = this.armedSwap;
      this.armedSwap = null;
    }
    if (this.armedSpell) {
      this.expectedSpell = this.armedSpell;
      this.armedSpell = null;
    }
    if (this.armedPlace) {
      this.expectedPlace = this.armedPlace;
      this.armedPlace = null;
    }
    if (this.armedPath) {
      this.expectedPath = this.armedPath;
      this.armedPath = null;
    }
    if (this.armedStartBattle) {
      this.expectedStartBattle = true;
      this.armedStartBattle = false;
    }
    if (this.armedConfirmSched) {
      this.expectedConfirmSched = true;
      this.armedConfirmSched = false;
    }
    if (this.armedPathToggle) {
      this.expectedPathToggle = true;
      this.armedPathToggle = false;
    }
    // **BUG FIX**: if a constraint just activated, DON'T advance the script
    // further — wait for the player to satisfy the constraint. Without this
    // guard, `_processStep` would walk past the next `expect*` step and
    // overwrite the freshly-activated constraint, leaving the player
    // unable to complete the tutorial.
    if (this._anyConstraintActive()) return;
    // If we're in lose-script and just finished the only dialogue step,
    // mark it done so Game can show the Retry button.
    if (this.phase === 'lose') {
      this._processStep();
      if (this.scriptIdx >= this.chapter.loseScript.length && !this.dialogueActive) {
        this.loseScriptDone = true;
      }
    } else {
      this._processStep();
    }
  }

  private _anyConstraintActive(): boolean {
    return this.expectedSwap != null
      || this.expectedSpell != null
      || this.expectedPlace != null
      || this.expectedPath != null
      || this.expectedStartBattle
      || this.expectedConfirmSched
      || this.expectedPathToggle;
  }

  /** Hook from Game.handleSwap right after a swap was successfully applied. */
  onSwapApplied(r1: number, c1: number, r2: number, c2: number): void {
    if (!this.expectedSwap) return;
    const e = this.expectedSwap;
    const matched =
      (e.r1 === r1 && e.c1 === c1 && e.r2 === r2 && e.c2 === c2) ||
      (e.r1 === r2 && e.c1 === c2 && e.r2 === r1 && e.c2 === c1);
    if (!matched) return;
    this.expectedSwap = null;
    // After-lines wait for cascade to settle (Game calls onMatchSettle).
    // _processStep() runs from inside onMatchSettle so the next presetBoard
    // doesn't drop in mid-animation.
  }

  /** Hook from Game when M3 cascade fully settles (board.locked === false). */
  onMatchSettle(): void {
    if (this.queuedAfterLines) {
      const lines = this.queuedAfterLines;
      this.queuedAfterLines = null;
      this._showDialogue(lines);
      return;
    }
    this._processStep();
  }

  /** Hook when a spell command is fully committed (slot + target).
   *  Handles wildcard el (-1) and wildcard targetPos (null) from
   *  `expectAnySpell` / `expectSpell` with `targetPos: null`. */
  onSpellApplied(slot: number, el: number, target: Pos): void {
    if (!this.expectedSpell) return;
    const e = this.expectedSpell;
    if (e.slot !== slot) return;
    if (e.el !== -1 && e.el !== el) return;
    if (e.targetPos !== null) {
      if (e.targetPos.x !== target.x || e.targetPos.y !== target.y) return;
    }
    this.expectedSpell = null;
    if (this.queuedAfterLines) {
      const lines = this.queuedAfterLines;
      this.queuedAfterLines = null;
      this._showDialogue(lines);
    } else {
      this._processStep();
    }
  }

  /** Hook from `_finalizeSched` (after player presses Confirm). */
  onSchedConfirmed(): void {
    if (this.expectedConfirmSched) {
      this.expectedConfirmSched = false;
      if (this.queuedAfterLines) {
        const lines = this.queuedAfterLines;
        this.queuedAfterLines = null;
        this._showDialogue(lines);
      } else {
        this._processStep();
      }
    }
    // Clear arrow hints so EXEC isn't cluttered.
    this.arrowHint = null;
  }

  /** Hook from Deployment.togglePathMode when player taps "Draw Paths". */
  onPathModeToggled(): void {
    if (!this.expectedPathToggle) return;
    this.expectedPathToggle = false;
    if (this.queuedAfterLines) {
      const lines = this.queuedAfterLines;
      this.queuedAfterLines = null;
      this._showDialogue(lines);
    } else {
      this._processStep();
    }
  }

  /** Hook from Game.startBattle (after the Start Battle button is pressed). */
  onStartBattle(): void {
    if (!this.expectedStartBattle) return;
    this.expectedStartBattle = false;
    if (this.queuedAfterLines) {
      const lines = this.queuedAfterLines;
      this.queuedAfterLines = null;
      this._showDialogue(lines);
    } else {
      this._processStep();
    }
  }

  /** Hook from Deployment.placeUnit when player successfully places a unit. */
  onUnitPlaced(type: string, x: number, y: number): void {
    if (!this.expectedPlace) return;
    if (this.expectedPlace.type !== type || this.expectedPlace.hintPos.x !== x || this.expectedPlace.hintPos.y !== y) return;
    this.expectedPlace = null;
    if (this.queuedAfterLines) {
      const lines = this.queuedAfterLines;
      this.queuedAfterLines = null;
      this._showDialogue(lines);
    } else {
      this._processStep();
    }
  }

  /** Hook from Deployment.addPathPoint when player adds a path cell.
   *  Called AFTER the cell is added — `currentPathLen` is the new length. */
  onPathPointAdded(currentPathLen: number): void {
    if (!this.expectedPath) return;
    if (currentPathLen < this.expectedPath.cells.length) return; // not complete yet
    // Path now matches expected length — assume cells match too (Game
    // gates each addPathPoint via isPathPointAllowed so out-of-order
    // additions are rejected at the source).
    this.expectedPath = null;
    if (this.queuedAfterLines) {
      const lines = this.queuedAfterLines;
      this.queuedAfterLines = null;
      this._showDialogue(lines);
    } else {
      this._processStep();
    }
  }

  /** Returns AI's pre-scripted spell schedule for `round` (1-indexed), or null. */
  aiScheduleFor(round: number): SpellCommand[] | null {
    const sched = this.chapter.aiSchedules?.[round];
    return sched ? sched.map((c) => ({ ...c, targetPos: c.targetPos ? { ...c.targetPos } : undefined })) : null;
  }

  /** Returns lines if an EXEC pause should fire BEFORE the given event.
   *  Marks the pause as fired so it doesn't repeat. */
  consumeExecPauseFor(event: { type: string; tick?: number; phase?: string }): DialogueLine[] | null {
    const pauses = this.chapter.execPauses;
    if (!pauses || pauses.length === 0) return null;
    for (const p of pauses) {
      if (p.beforeTick !== event.tick) continue;
      if (p.beforeType !== event.type) continue;
      if (p.beforePhase && p.beforePhase !== (event as any).phase) continue;
      const key = `${p.beforeTick}:${p.beforeType}:${p.beforePhase || ''}`;
      if (this.firedExecPauses.has(key)) continue;
      this.firedExecPauses.add(key);
      return p.lines;
    }
    return null;
  }

  /** Game queries this each frame during EXEC — if true, pause playback. */
  shouldPauseExec(): boolean {
    return this.dialogueActive;
  }

  /** Hook from Game when a macro phase boundary is crossed. If the
   *  script is parked on a `waitForPhase` for this phase, advance past
   *  it and resume normal step processing (which usually shows the
   *  next dialogue / arms the next constraint). */
  notifyPhaseEntered(phase: 'match3' | 'sched' | 'exec' | 'over'): void {
    if (this.waitingForPhase !== phase) return;
    this.waitingForPhase = null;
    this.scriptIdx++;
    this._processStep();
  }

  /** Show a dialogue at runtime (called by Game when EXEC hits a pause). */
  showRuntimeDialogue(lines: DialogueLine[]): void {
    this._showDialogue(lines);
  }

  /** Hook from `_applyEvent` — fire conditional tips for matching events. */
  observeBattleEvent(
    event: { type: string; [k: string]: any },
    ctxData: {
      units: Array<{ id: string; type: string; pos: Pos }>;
      terrainAt: (x: number, y: number) => string;
    },
  ): void {
    if (this.battleOver) return;                          // Don't fire after game over
    if (!this.freePlay && this.phase === 'main') return; // Tips only post-script
    if (this.dialogueActive) return;
    for (const tip of this.chapter.conditionalTips) {
      if (this.shownTips.has(tip.id)) continue;
      if (this._tipMatches(tip, event, ctxData)) {
        this.shownTips.add(tip.id);
        this._showDialogue(tip.lines);
        return; // Only one tip per event tick
      }
    }
  }

  /** Hook from Game._endGame — branch into post or lose script. */
  onGameOver(winner: number, myTeam: number): void {
    this.battleOver = true;
    this.scriptIdx = 0;
    if (winner === myTeam) {
      this.phase = 'post';
      StoryProgress.markCompleted(this.chapter.id);
    } else {
      this.phase = 'lose';
    }
    this.expectedSwap = null;
    this.expectedSpell = null;
    this.arrowHint = null;
    this._processStep();
  }

  // ----------------------------------------------------------
  // Internal — script walker
  // ----------------------------------------------------------

  private _currentScript(): TutorialStep[] {
    if (this.phase === 'post') return this.chapter.postBattleScript;
    if (this.phase === 'lose') return this.chapter.loseScript;
    if (this.phase === 'done') return [];
    return this.chapter.script;
  }

  /**
   * Walk forward through the script, executing each step until either:
   *   - dialogue must be shown (waits for player tap)
   *   - a constraint becomes active (waits for player action)
   *   - we run off the end
   */
  private _processStep(): void {
    if (this.dialogueActive) return;
    const script = this._currentScript();
    while (this.scriptIdx < script.length) {
      const step = script[this.scriptIdx];
      if (step.kind === 'dialogue') {
        this.scriptIdx++;
        this._showDialogue(step.lines);
        return;
      }
      if (step.kind === 'presetDeployment') {
        this.pendingDeployment = { player: step.player, ai: step.ai };
        this.scriptIdx++;
        return;
      }
      if (step.kind === 'presetBoard') {
        this.pendingBoard = step.board;
        this.scriptIdx++;
        return;
      }
      if (step.kind === 'arrowHint') {
        this.arrowHint = { from: step.from, to: step.to, label: step.label };
        this.scriptIdx++;
        if (step.lines && step.lines.length > 0) {
          this._showDialogue(step.lines);
        }
        return;
      }
      if (step.kind === 'expectSwap') {
        const swap: ExpectedSwap = {
          r1: step.r1, c1: step.c1, r2: step.r2, c2: step.c2,
          hint: step.hint,
        };
        this.queuedAfterLines = step.after || null;
        this.scriptIdx++;
        if (step.lines && step.lines.length > 0) {
          this.armedSwap = swap;
          this._showDialogue(step.lines);
        } else {
          this.expectedSwap = swap;
        }
        return;
      }
      if (step.kind === 'expectSpell') {
        const spell: ExpectedSpell = {
          slot: step.slot, el: step.el, targetPos: step.targetPos,
          hint: step.hint,
        };
        this.queuedAfterLines = step.after || null;
        this.scriptIdx++;
        if (step.lines && step.lines.length > 0) {
          this.armedSpell = spell;
          this._showDialogue(step.lines);
        } else {
          this.expectedSpell = spell;
        }
        return;
      }
      if (step.kind === 'expectAnySpell') {
        // Wildcard: accept ANY element + ANY target for the slot.
        const spell: ExpectedSpell = {
          slot: step.slot, el: -1, targetPos: null,
        };
        this.queuedAfterLines = step.after || null;
        this.scriptIdx++;
        if (step.lines && step.lines.length > 0) {
          this.armedSpell = spell;
          this._showDialogue(step.lines);
        } else {
          this.expectedSpell = spell;
        }
        return;
      }
      if (step.kind === 'expectPlace') {
        const p: ExpectedPlace = { type: step.type, hintPos: step.hintPos, hint: step.hint };
        this.queuedAfterLines = step.after || null;
        this.scriptIdx++;
        if (step.lines && step.lines.length > 0) {
          this.armedPlace = p;
          this._showDialogue(step.lines);
        } else {
          this.expectedPlace = p;
        }
        return;
      }
      if (step.kind === 'expectPath') {
        const p: ExpectedPath = {
          unitPos: step.unitPos,
          cells: step.cells.map((c) => ({ x: c.x, y: c.y })),
          hint: step.hint,
        };
        this.queuedAfterLines = step.after || null;
        this.scriptIdx++;
        if (step.lines && step.lines.length > 0) {
          this.armedPath = p;
          this._showDialogue(step.lines);
        } else {
          this.expectedPath = p;
        }
        return;
      }
      if (step.kind === 'expectStartBattle') {
        this.queuedAfterLines = step.after || null;
        this.scriptIdx++;
        if (step.lines && step.lines.length > 0) {
          this.armedStartBattle = true;
          this._showDialogue(step.lines);
        } else {
          this.expectedStartBattle = true;
        }
        return;
      }
      if (step.kind === 'expectConfirmSched') {
        this.queuedAfterLines = step.after || null;
        this.scriptIdx++;
        if (step.lines && step.lines.length > 0) {
          this.armedConfirmSched = true;
          this._showDialogue(step.lines);
        } else {
          this.expectedConfirmSched = true;
        }
        return;
      }
      if (step.kind === 'expectPathToggle') {
        this.queuedAfterLines = step.after || null;
        this.scriptIdx++;
        if (step.lines && step.lines.length > 0) {
          this.armedPathToggle = true;
          this._showDialogue(step.lines);
        } else {
          this.expectedPathToggle = true;
        }
        return;
      }
      if (step.kind === 'freePlay') {
        this.freePlay = true;
        this.arrowHint = null;
        this.scriptIdx++;
        // Fall through — keep processing in case more steps follow
        continue;
      }
      if (step.kind === 'waitForPhase') {
        // Park the script walker until Game notifies that the target
        // phase has been entered (via notifyPhaseEntered). The step
        // index does NOT advance here — `notifyPhaseEntered` advances
        // it once the awaited phase fires.
        this.waitingForPhase = step.phase;
        return;
      }
    }
    // End of current script — nothing more to drive.
    if (this.phase === 'post') {
      this.phase = 'done';
    }
  }

  private _showDialogue(lines: DialogueLine[]): void {
    if (lines.length === 0) return;
    this.dialogueLines = lines;
    this.dialogueIdx = 0;
    this.dialogueActive = true;
  }

  private _tipMatches(
    tip: ConditionalTip,
    event: { type: string; [k: string]: any },
    ctxData: {
      units: Array<{ id: string; type: string; pos: Pos }>;
      terrainAt: (x: number, y: number) => string;
    },
  ): boolean {
    const findUnit = (id: string) => ctxData.units.find((u) => u.id === id);
    switch (tip.kind) {
      case 'archerOnGrass': {
        // CombatEvent.groups[].attacks[] — see server/types.ts AttackRecord.
        if (event.type !== 'combat') return false;
        const groups = (event.groups as Array<any> | undefined) || [];
        for (const g of groups) {
          for (const a of (g.attacks || [])) {
            const attacker = findUnit(a.attackerId);
            if (!attacker || attacker.type !== 'Archer') continue;
            // Skip base attacks (targetUnitId is null for base hits).
            if (!a.targetUnitId || !a.targetUnitPos) continue;
            const t = ctxData.terrainAt(a.targetUnitPos.x, a.targetUnitPos.y);
            if (t === 'grass' || t === 'burningGrass') return true;
          }
        }
        return false;
      }
      case 'lightningOnPuddle': {
        // SpellEvent.spells[] is SpellCastRecord — spellType lives on cmd.
        if (event.type !== 'spell') return false;
        const spells = (event.spells as Array<any> | undefined) || [];
        for (const s of spells) {
          const cmdEl = s.cmd?.spellType;
          if (cmdEl !== 5) continue; // 5 = Lightning
          for (const imp of (s.impacts || [])) {
            const target = findUnit(imp.unitId);
            if (!target) continue;
            const t = ctxData.terrainAt(target.pos.x, target.pos.y);
            if (t === 'puddle') return true;
          }
        }
        return false;
      }
      case 'fireIgnitesGrass':
        return event.type === 'terrainIgnite' &&
               Array.isArray(event.cells) && event.cells.length > 0;
      case 'cavalryInPuddle': {
        // MoveEvent.moves[] is MoveRecord with from / to in view coords.
        if (event.type !== 'move') return false;
        const moves = (event.moves as Array<any> | undefined) || [];
        for (const m of moves) {
          const u = findUnit(m.unitId);
          if (!u || u.type !== 'Cavalry') continue;
          const finalPos = m.to ?? u.pos;
          if (!finalPos) continue;
          const t = ctxData.terrainAt(finalPos.x, finalPos.y);
          if (t === 'puddle') return true;
        }
        return false;
      }
      case 'contestRetreat':
        // ContestResolveEvent — only fire if at least one retreat actually
        // happened (an empty retreats list means contest pair was already
        // dissolved by deaths).
        return event.type === 'contestResolve' &&
               Array.isArray(event.retreats) && event.retreats.length > 0;
    }
    return false;
  }
}
